import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Segment } from '../types';
import { Meal } from '../../Meal/types';
import { SelectedMeal } from '../../MealPage/types';
import ServiceWithSegments from '../../ServiceWithSegments/ServiceWithSegments';
import ServiceWrapper from './ServiceWrapper/ServiceWrapper';
import { getSegmentsWithMeal } from '../utils';
import { useTheme } from '../../theme';
import { NULL_COMPONENT, useRenderers } from '../../renderProps';
import { useCallback } from 'react';

interface Props {
	meals: Meal[];
	segments: Segment[];
	mealRenderer: (meal: Meal, segmentId: string, isSingleMeal?: boolean) => React.ReactNode;
	onMobileProceed: () => void;
	onMobileBack: () => void;
	selectedMeals?: SelectedMeal[];
	initialSelectedMeals?: SelectedMeal[];
	isLoading?: boolean;
	onClose?: () => void;
	segmentId?: string;
	activeSegmentId?: string;
	onSegmentChange?: (segmentId: string) => void;
}

const MealService: React.FunctionComponent<Props> = ({
	selectedMeals = [],
	initialSelectedMeals = [],
	isLoading,
	mealRenderer,
	onMobileProceed,
	onMobileBack,
	meals,
	segments,
	onClose,
	segmentId,
	activeSegmentId,
	onSegmentChange
}) => {
	const { t } = useTranslation('MealPage');
	const { ServiceWrapper: css } = useTheme('MealService');

	const segmentsWithMeal = getSegmentsWithMeal(meals);
	const firstSegmentWithMeal = segmentId || [...segmentsWithMeal].sort().shift();

	const {
		MealServiceWrapper: MealServiceWrapperOverride = ServiceWrapper,
		MealServiceAdditionalInfo: MealServiceAdditionalInfoOverride = NULL_COMPONENT
	} = useRenderers();

	const renderAdditionalInfo = useCallback(() => <MealServiceAdditionalInfoOverride />, []);

	return (
		<ServiceWithSegments
			title={t('Feel the taste in flight')}
			initialSegmentId={firstSegmentWithMeal}
			activeSegmentId={activeSegmentId}
			onSegmentChange={onSegmentChange}
			isLoading={isLoading}
			onMobileProceed={onMobileProceed}
			onMobileBack={onMobileBack}
			enabledSegments={segmentsWithMeal}
			segments={segments.map(segment => ({
				id: segment.id,
				title: `${segment.departure.airport.city.name} — ${segment.arrival.airport.city.name}`,
				isDisabled: !segmentsWithMeal.has(segment.id),
				isChecked: selectedMeals.some(({ segmentId }) => segmentId === segment.id)
			}))}
			onClose={onClose}
			className={css.meal}
			renderAdditionalInfo={renderAdditionalInfo}
		>
			{({ activeSegmentId }) => (
				<MealServiceWrapperOverride
					activeSegmentId={activeSegmentId}
					mealRenderer={mealRenderer}
					meals={meals}
					selectedMeals={selectedMeals}
					singleMealVariant="standard"
				/>
			)}
		</ServiceWithSegments>
	);
};

export default MealService;
